/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
// ============================================================
// HERO SECTION - Lightswind 3D Slider Integration (Responsive)
// ============================================================

import { useMemo } from "react";
import ThreeDSlider from "../../../components/lightswind/3d-slider.jsx";

export default function HeroSection({
  storeData = {},
  apiProducts = [],
  apiCategories = [],
  displayBanners = [],
  storeReviews = [],
  theme,
}) {
  const safeTheme = theme || { primary: "#EC4899", accent: "#FCD34D" };
  const safeStore = storeData || {};

  const avgRating =
    storeReviews.length > 0
      ? (
        storeReviews.reduce((s, r) => s + Number(r.rating || 0), 0) /
        storeReviews.length
      ).toFixed(1)
      : "0";

  const activeBanners = useMemo(
    () => (Array.isArray(displayBanners) ? displayBanners : []).filter((b) => b.active),
    [displayBanners]
  );

  const sliderItems = useMemo(() => {
    const base = activeBanners.map((banner, index) => ({
      title: banner.name || safeStore.storeName || "العرض",
      num: (banner.badge || banner.price || `${index + 1}`).toString().padStart(2, "0"),
      imageUrl: banner.imageUrl || "https://placehold.co/1200x800",
      data: banner,
    }));
    if (base.length === 0) return [];
    // ensure at least 5 لنعطي عمق واضح
    const filled = [...base];
    let i = 0;
    while (filled.length < 5) {
      const src = base[i % base.length];
      filled.push({ ...src, num: `${filled.length + 1}`.padStart(2, "0") });
      i++;
    }
    return filled;
  }, [activeBanners, safeStore]);

  return (
    <section className="relative overflow-hidden border-b border-white/10 bg-[#0f0f0f]">
      {/* خلفيات */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900/25 via-transparent to-pink-900/25 pointer-events-none" />
      <div className="absolute -left-32 top-10 w-64 h-64 sm:w-80 sm:h-80 bg-purple-500/15 blur-[120px] rounded-full" />
      <div className="absolute -right-24 -bottom-10 w-72 h-72 sm:w-96 sm:h-96 bg-pink-500/15 blur-[140px] rounded-full" />

      <div className="relative max-w-6xl mx-auto px-3 sm:px-6 py-7 sm:py-14 lg:py-18">
        <div className="grid md:grid-cols-2 gap-6 sm:gap-10 lg:gap-14 items-center">
          {/* النصوص */}
          <div className="space-y-5 sm:space-y-7 text-center md:text-right order-2 md:order-1">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/10 text-[10px] sm:text-[11px] text-white/70 border border-white/10">
              <span className="w-2 h-2 rounded-full" style={{ background: safeTheme.accent }} />
              متاجر موثوقة – شحن سريع وآمن
            </div>

            <div className="space-y-2.5 sm:space-y-3">
              <h2 className="text-xl sm:text-4xl md:text-5xl font-black text-white leading-tight">
                أهلاً بكم في{" "}
                <span
                  className="inline-block bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"
                  dir="ltr"
                >
                  {safeStore.storeName || safeStore.ClientName || "Our Store"}
                </span>
              </h2>

              <p className="text-xs sm:text-base text-white/70">
                {safeStore.storeSlogan || safeStore.SectorType || "تسوق إلكتروني مميز"} – اختيارات
                منتقاة، عروض حصرية، وتجربة سلسة على كل الأجهزة.
              </p>
            </div>

            {/* إحصائيات */}
            <div className="w-full max-w-2xl mx-auto flex flex-wrap justify-center gap-4 sm:gap-5 md:gap-6">
              <StatCard label="منتج" value={`${apiProducts.length}+`} icon="📦" color={safeTheme.accent} />
              <StatCard label="فئة" value={`${apiCategories.length}+`} icon="🏷️" color={safeTheme.accent} />
              <StatCard label="تقييم" value={`${avgRating}★`} icon="⭐" color={safeTheme.accent} />
            </div>

            {/* المزايا */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-4 max-w-lg mx-auto md:ml-auto">
              <FeatureCard icon="💳" title="دفع آمن" description="حماية 100%" compact tight />
              <FeatureCard icon="🚚" title="توصيل سريع" description="لجميع المناطق" compact tight />
              <FeatureCard icon="🎁" title="عروض حصرية" description="قسائم وخصومات" compact tight />
              <FeatureCard icon="🛟" title="دعم فوري" description="24/7 لخدمتك" compact tight />
            </div>
          </div>

          {/* السلايدر */}
          <div className="relative order-1 md:order-2 flex justify-center items-center py-6 sm:py-10 w-full">
            <div className="w-full max-w-[760px] mx-auto relative">
              <div className="absolute inset-4 sm:inset-6 rounded-[30px] bg-gradient-to-r from-purple-500/25 via-pink-500/18 to-amber-400/18 blur-3xl -z-10" />
              {sliderItems.length > 0 ? (
                <ThreeDSlider
                  items={sliderItems}
                  containerStyle={{
                    height: "min(70vh, 440px)",
                    minHeight: "260px",
                    margin: "0 auto",
                    background: "transparent",
                  }}
                />
              ) : (
                <div className="w-full h-[260px] bg-white/5 rounded-3xl border border-dashed border-white/20 flex items-center justify-center text-white/40 text-sm sm:text-base">
                  لا يوجد بنرات متاحة حالياً
                </div>
              )}
            </div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-52 sm:w-64 h-52 sm:h-64 bg-purple-500/20 blur-[90px] -z-10" />
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// STAT CARD COMPONENT
// ============================================================
const StatCard = ({ label, value, icon, color, small, tiny }) => (
  <div
    className={`flex flex-col items-center gap-1 ${tiny ? "px-2.5 py-2" : small ? "px-3 py-2.5" : "px-5 py-4"
      } rounded-2xl bg-white/5 backdrop-blur-sm border border-white/12 hover:bg-white/10 transition-all duration-300`}
  >
    <div className={tiny ? "text-lg" : small ? "text-xl" : "text-3xl"}>{icon}</div>
    <div className={tiny ? "text-lg font-black" : small ? "text-xl font-black" : "text-2xl font-black"} style={{ color }}>
      {value}
    </div>
    <div className="text-[10px] sm:text-sm text-white/70 font-semibold">{label}</div>
  </div>
);

// ============================================================
// FEATURE CARD COMPONENT
// ============================================================
const FeatureCard = ({ icon, title, description, compact, tight }) => (
  <div
    className={`flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/10 hover:bg-white/15 transition-all ${tight ? "px-3 py-2.5 sm:px-4" : compact ? "px-3 py-3 sm:px-4" : "px-4 py-3"
      }`}
  >
    <span className={tight ? "text-lg" : compact ? "text-xl" : "text-2xl"}>{icon}</span>
    <div className="flex-1 text-right">
      <div
        className={
          tight ? "text-xs font-bold text-white" : compact ? "text-sm font-bold text-white" : "text-sm sm:text-base font-bold text-white"
        }
      >
        {title}
      </div>
      <div className={tight ? "text-[10px] text-white/65" : compact ? "text-[11px] text-white/65" : "text-xs text-white/65"}>
        {description}
      </div>
    </div>
  </div>
);
